import { t, Trans } from '@lingui/macro';
import HelpIcon from '@material-ui/icons/Help';
import HelpOutline from '@material-ui/icons/HelpOutline';
import { FormControlLabel, Radio, RadioGroup } from '@material-ui/core';
import { useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import Typography from '@material-ui/core/Typography';
import { useTestTypeContext } from '../hooks/UseTestTypeContext';
import { useHelpUrl } from '../../../../../utilities/HelpUtilities';
import { useDetailStyles } from '../../../../panels/net-test-now-panels/NetTestNowStyles';
import { VOIP_PHONE_TEST_TYPE } from '../../../../panels/voip-test-now-panels/VoipTestConstants';
import DataTable from '../../../../data-table/DataTable';
import { useArrayLoader } from '../../../../data-table/Loaders';
import {
    buildServiceTestAgentPickerGridColumns,
    SERVICE_TEST_DATA_ARRAY_SEARCH_FIELDS,
} from '../../../../panels/net-test-now-panels/NetTestNowHelpers';
import { useColumnStyles } from '../../../agents/AgentStyles';
import { initialSort } from '../../../../panels/net-test-now-panels/NetTestNowAgentsFormSection';
import { filterEntities } from '../../../../form/ListSelectorUtils';
import { useSelectorStyles } from '../../../../form/ListSelectorStyles';
import {
    ADAPTER_TYPE_FIELD_NAME,
    ALL_MODE,
    DEFAULT,
    MULTI,
    TAGGED_MODE,
    WIFI_TEST_TYPES,
    WIRELESS,
} from '../../ServiceTestsConstants';
import { SHOULD_DIRTY_TRUE } from '../../ServiceTestsFormControlConstants';
import { StyledTooltip } from '../../../../panels/net-test-now-panels/NetTestNowAgentTagSelect';
import NgpRow from '../../../../form/NgpRow';
import TestSubFormSection from './TestFormSection/TestSubFormSection';
import useAgentsContext from '../hooks/AgentsContextProvider';

const TestnPointInterfaceContent = ({ adapterType, onAdapterTypeChange }) => {
    const styles = useDetailStyles();
    const { typeName } = useTestTypeContext();
    const { setValue } = useFormContext();

    // adapterType useEffect is NOT needed here. portal/ui's agentsByTestType() uses if...else if
    // so wifiConnectTest types always get dynamicWifi=true regardless of adapterType value.
    // buildAgentFilterParams mirrors this if...else if logic. All WiFi types get adapterType
    // forced to 'wireless' at save time only (see wifiSaveTransform in TestDetailPanel).

    if (typeName === VOIP_PHONE_TEST_TYPE || WIFI_TEST_TYPES.has(typeName)) {
        return (
            <div>
                <Typography gutterBottom>
                    <Trans>Note: This test type does not support interface selection</Trans>
                </Typography>
            </div>
        );
    }

    return (
        <RadioGroup
            className={styles.radioWrapper}
            value={adapterType}
            onChange={(e) => {
                setValue(ADAPTER_TYPE_FIELD_NAME, e.target.value, SHOULD_DIRTY_TRUE);
                onAdapterTypeChange?.(e.target.value);
            }}
        >
            <FormControlLabel value={DEFAULT} label={t`Run on nPoint wired interface`} control={<Radio />} />
            <FormControlLabel
                value={WIRELESS}
                label={t`Run on hardware nPoint wireless interface`}
                control={<Radio />}
            />
            <FormControlLabel value={MULTI} label={t`Run only when part of a Wi-Fi Group Test`} control={<Radio />} />
        </RadioGroup>
    );
};

TestnPointInterfaceContent.propTypes = {
    adapterType: PropTypes.string,
    onAdapterTypeChange: PropTypes.func,
};

TestnPointInterfaceContent.defaultProps = {
    adapterType: undefined,
    onAdapterTypeChange: undefined,
};

/**
 * MatchingNPointsSection
 *
 * Renders a preview table of nPoints matching current tag selection.
 */
const MatchingNPointsSection = () => {
    const { matchingTaggedAgents } = useAgentsContext();
    const { typeName } = useTestTypeContext();
    const columnStyles = useColumnStyles();
    const selectorStyles = useSelectorStyles();

    const showVoipRegistration = typeName === VOIP_PHONE_TEST_TYPE;

    const staticDataFilterCallback = useCallback(
        (entities, filters) => filterEntities(entities, filters, SERVICE_TEST_DATA_ARRAY_SEARCH_FIELDS),
        []
    );

    const taggedAgentsLoader = useArrayLoader(matchingTaggedAgents, staticDataFilterCallback);

    return (
        <TestSubFormSection
            useTestSmallSubFormSection
            addSpacer
            title={
                <NgpRow>
                    <Trans>Matching nPoints</Trans> &nbsp;&nbsp;&nbsp;&nbsp;
                    <StyledTooltip
                        placement="right"
                        title={<Trans>nPoints that match the selected tags based on the tag criteria above</Trans>}
                    >
                        <HelpOutline fontSize="small" />
                    </StyledTooltip>
                </NgpRow>
            }
        >
            <div className={selectorStyles.selectorContainer}>
                <DataTable
                    loader={taggedAgentsLoader}
                    columns={buildServiceTestAgentPickerGridColumns(columnStyles, showVoipRegistration)}
                    disableCSVExport
                    disableColumnRemover
                    sorters={initialSort}
                    emptyText={t`Add nPoint tags above to identify nPoints to run this service test`}
                />
            </div>
        </TestSubFormSection>
    );
};

const TestnPointInterfaceSection = ({ adapterType, mode, onAdapterTypeChange }) => {
    const useTestSmallSubFormSection = mode === ALL_MODE;
    const nPointInterfaceSectionUrl = useHelpUrl('ServiceTests');

    return (
        <>
            <TestSubFormSection
                useTestSmallSubFormSection={useTestSmallSubFormSection}
                title={
                    <NgpRow>
                        <Trans>nPoint Interface</Trans> &nbsp;&nbsp;&nbsp;&nbsp;
                        <a
                            href={nPointInterfaceSectionUrl}
                            target="_blank"
                            rel="noreferrer"
                            title={t`View help documentation regarding nPoints selection`}
                        >
                            <HelpIcon fontSize="small" />
                        </a>
                    </NgpRow>
                }
            >
                <TestnPointInterfaceContent adapterType={adapterType} onAdapterTypeChange={onAdapterTypeChange} />
            </TestSubFormSection>
            {mode === TAGGED_MODE && <MatchingNPointsSection />}
        </>
    );
};

TestnPointInterfaceSection.propTypes = {
    adapterType: PropTypes.string,
    mode: PropTypes.string,
    onAdapterTypeChange: PropTypes.func,
};

TestnPointInterfaceSection.defaultProps = {
    adapterType: undefined,
    mode: undefined,
    onAdapterTypeChange: undefined,
};

export default TestnPointInterfaceSection;
